import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi, AdminUser } from '../api/admin';
import { useAdminSSE } from '../hooks/useAdminSSE';
import { Users, Mail, Calendar, Tag, Building2, CheckCircle, XCircle, ChevronLeft, ChevronRight, Briefcase, Eye } from 'lucide-react';

type SortBy = 'createdAt' | 'updatedAt';
type SortOrder = 'asc' | 'desc';
type ReferralFilter = 'all' | 'yes' | 'no';

export default function AdminUsersPage() {
  const { t } = useTranslation();
  useAdminSSE();
  const [sortBy, setSortBy] = useState<SortBy>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [referralFilter, setReferralFilter] = useState<ReferralFilter>('all');
  const [page, setPage] = useState(1);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', sortBy, sortOrder, referralFilter, page],
    queryFn: () => adminApi.listUsers({
      sortBy,
      sortOrder,
      page,
      limit,
      hasReferral: referralFilter === 'all' ? undefined : referralFilter === 'yes',
    }),
  });

  const users = data?.users || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status?: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      trialing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      past_due: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      canceled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      unpaid: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[status || ''] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Admin Navigation */}
        <div className="flex gap-4 mb-6">
          <Link
            to="/admin/affiliates"
            className="px-4 py-2 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Briefcase size={18} />
            {t('admin.affiliates.title', 'Affiliates')}
          </Link>
          <span className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium flex items-center gap-2">
            <Users size={18} />
            {t('admin.users.title', 'Users')}
          </span>
          <Link
            to="/admin/visits"
            className="px-4 py-2 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Eye size={18} />
            {t('admin.visits.title', 'Visits')}
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">{t('admin.users.title', 'Users')}</h1>
            <span className="text-gray-400">({total})</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          {/* Sort by */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">{t('admin.users.sortBy', 'Sort by')}:</label>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value as SortBy); setPage(1); }}
              className="bg-dark-800 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="createdAt">{t('admin.users.registered', 'Registered')}</option>
              <option value="updatedAt">{t('admin.users.updated', 'Updated')}</option>
            </select>
          </div>

          {/* Sort order */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">{t('admin.users.order', 'Order')}:</label>
            <select
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value as SortOrder); setPage(1); }}
              className="bg-dark-800 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="desc">{t('admin.users.newest', 'Newest first')}</option>
              <option value="asc">{t('admin.users.oldest', 'Oldest first')}</option>
            </select>
          </div>

          {/* Referral filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">{t('admin.users.referral', 'Referral')}:</label>
            <select
              value={referralFilter}
              onChange={(e) => { setReferralFilter(e.target.value as ReferralFilter); setPage(1); }}
              className="bg-dark-800 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">{t('admin.users.all', 'All')}</option>
              <option value="yes">{t('admin.users.withReferral', 'With referral')}</option>
              <option value="no">{t('admin.users.withoutReferral', 'Without referral')}</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mx-auto" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              {t('admin.users.noUsers', 'No users found')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">
                      {t('admin.users.email', 'Email')}
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">
                      {t('admin.users.status', 'Status')}
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">
                      {t('admin.users.referredBy', 'Referral')}
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">
                      {t('admin.users.business', 'Business')}
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">
                      {t('admin.users.registered', 'Registered')}
                    </th>
                    <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">
                      {t('admin.users.verified', 'Verified')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((user: AdminUser) => (
                    <tr key={user._id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <div>
                            <div className="text-white font-medium">{user.email}</div>
                            {user.username && (
                              <div className="text-xs text-gray-500">@{user.username}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.subscriptionStatus ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(user.subscriptionStatus)}`}>
                            {user.subscriptionStatus}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.referredBy ? (
                          <div className="flex items-center gap-1">
                            <Tag className="w-4 h-4 text-primary-400" />
                            <span className="text-primary-400 font-mono text-sm">{user.referredBy}</span>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.profile?.companyName || user.profile?.venueName ? (
                          <div className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-300 text-sm">
                              {user.profile.companyName || user.profile.venueName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(user.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.emailVerifiedAt ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-400">
              {t('admin.users.showing', 'Showing')} {(page - 1) * limit + 1}–{Math.min(page * limit, total)} {t('admin.users.of', 'of')} {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg bg-white/5 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-white px-3">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg bg-white/5 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
